import {
  Box,
  Button,
  type ConditionalValue,
  Select,
  Separator,
  Skeleton,
  Stack,
  createListCollection,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus } from 'react-icons/fa';

import {
  type GlobalReferentialCategoryPublic,
  type GlobalReferentialIndicatorUpdate,
  type GlobalReferentialPublic,
  type GlobalReferentialSubCategoryPublic,
  UtilsService,
} from '@/client';

interface ApiResponse {
  data: GlobalReferentialPublic[];
  count: number;
}

function getReferentialsQueryOptions({ slug }: { slug: string }) {
  return {
    queryKey: ['referentials', { slug }],
    queryFn: async () => await UtilsService.readReferentials({ companySlug: slug }),
  };
}

export const SelectReferentials = ({
  value,
  onChange,
  disabledAdd = false,
  size = 'xs',
}: {
  value: GlobalReferentialIndicatorUpdate[];
  onChange: (value: GlobalReferentialIndicatorUpdate[]) => void;
  disabledAdd?: boolean;
  size?: ConditionalValue<'sm' | 'md' | 'lg' | 'xs' | undefined>;
}) => {
  const { slug } = useParams({ strict: false });
  const { data: referentialsData, isLoading } = useQuery<ApiResponse>({
    ...getReferentialsQueryOptions({ slug }),
  });
  const { t } = useTranslation();

  // Store each referential, category, and sub-category selection separately
  const [selectedReferentials, setSelectedReferentials] = useState<GlobalReferentialIndicatorUpdate[]>(
    value.map((el) => ({
      id: el.id ?? null,
      global_referential_id: el.global_referential_id ?? null,
      global_referential_category_id: el.global_referential_category_id ?? null,
      global_referential_sub_category_id: el.global_referential_sub_category_id ?? null,
    }))
  );

  const getReferentialCollection = () => {
    return createListCollection({
      items: referentialsData?.data || [],
      itemToString: (item: GlobalReferentialPublic) => item.name,
      itemToValue: (item: GlobalReferentialPublic) => item.id,
    });
  };

  const getCategoriesCollection = (referential_id: string) => {
    const referentialData = referentialsData?.data.find((r) => r.id === referential_id);
    return createListCollection({
      items: referentialData?.global_referential_category || [],
      itemToString: (item: GlobalReferentialCategoryPublic) => item.name,
      itemToValue: (item: GlobalReferentialCategoryPublic) => item.id,
    });
  };

  const getSubCategoriesCollection = (referential_id: string, category_id: string) => {
    const referentialData = referentialsData?.data.find((r) => r.id === referential_id);
    const categoryData = referentialData?.global_referential_category.find((c) => c.id === category_id);
    return createListCollection({
      items: categoryData?.global_referential_sub_category || [],
      itemToString: (item: GlobalReferentialSubCategoryPublic) => item.name,
      itemToValue: (item: GlobalReferentialSubCategoryPublic) => item.id,
    });
  };

  // Handle adding a new referential
  const handleAddReferential = () => {
    setSelectedReferentials([
      ...selectedReferentials,
      {
        id: null,
        global_referential_id: null,
        global_referential_category_id: null,
        global_referential_sub_category_id: null,
      },
    ]);
  };

  const handleRemoveReferential = (index: number) => {
    const updatedSelections = [...selectedReferentials];
    updatedSelections.splice(index, 1);
    setSelectedReferentials(updatedSelections);
    onChange(updatedSelections);
  };

  if (disabledAdd && selectedReferentials.length === 0) {
    handleAddReferential();
  }

  return (
    <>
      {isLoading ? (
        <Skeleton height="8" />
      ) : (
        <Stack w={'full'}>
          {/* Render the selectors dynamically for each referential */}
          {selectedReferentials.map((referentialSelection, index) => {
            const referentialCollection = getReferentialCollection();

            const categoriesCollection = getCategoriesCollection(referentialSelection.global_referential_id ?? '');

            const subCategoriesCollection = getSubCategoriesCollection(
              referentialSelection.global_referential_id ?? '',
              referentialSelection.global_referential_category_id ?? ''
            );

            return (
              <Box key={referentialSelection.id || index}>
                {/* Global Referential Selector */}
                <Select.Root
                  collection={referentialCollection}
                  size={size}
                  w="full"
                  value={[referentialSelection.global_referential_id ?? '']}
                  onValueChange={(e) => {
                    const updatedSelections = [...selectedReferentials];
                    updatedSelections[index] = {
                      ...updatedSelections[index],
                      global_referential_id: e.value[0] ?? null,
                      global_referential_category_id: null, // Reset category and sub-category
                      global_referential_sub_category_id: null,
                    };
                    setSelectedReferentials(updatedSelections);
                    onChange(updatedSelections);
                  }}
                >
                  <Select.Control>
                    <Select.Trigger>
                      <Select.ValueText placeholder={t('indicator.filter.referentials.select')} />
                    </Select.Trigger>
                    <Select.IndicatorGroup>
                      <Select.ClearTrigger onClick={() => handleRemoveReferential(index)} />
                      <Select.Indicator />
                    </Select.IndicatorGroup>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content>
                      {referentialCollection.items.map((referential) => (
                        <Select.Item item={referential} key={referential.name}>
                          {referential.name}
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>

                {/* Category Selector */}
                {categoriesCollection.items.length > 0 && (
                  <Select.Root
                    collection={categoriesCollection}
                    size={size}
                    w="calc(100% - {spacing.4})"
                    ml={4}
                    value={
                      referentialSelection.global_referential_category_id
                        ? [referentialSelection.global_referential_category_id]
                        : []
                    }
                    onValueChange={(e) => {
                      const updatedSelections = [...selectedReferentials];
                      updatedSelections[index] = {
                        ...updatedSelections[index],
                        global_referential_category_id: e.value[0] ?? null,
                        global_referential_sub_category_id: null, // Reset sub-category
                      };
                      setSelectedReferentials(updatedSelections);
                      onChange(updatedSelections);
                    }}
                    disabled={!referentialSelection.global_referential_id}
                  >
                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText placeholder={t('indicator.filter.category.select')} />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.ClearTrigger />
                        <Select.Indicator />
                      </Select.IndicatorGroup>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content>
                        {categoriesCollection.items.map((category) => (
                          <Select.Item item={category} key={category.name}>
                            {category.name}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                )}

                {/* Sub-Category Selector */}
                {subCategoriesCollection.items.length > 0 && (
                  <Select.Root
                    collection={subCategoriesCollection}
                    size={size}
                    w="calc(100% - {spacing.8})"
                    ml={8}
                    value={
                      referentialSelection.global_referential_sub_category_id
                        ? [referentialSelection.global_referential_sub_category_id]
                        : []
                    }
                    onValueChange={(e) => {
                      const updatedSelections = [...selectedReferentials];
                      updatedSelections[index] = {
                        ...updatedSelections[index],
                        global_referential_sub_category_id: e.value[0] ?? null,
                      };
                      setSelectedReferentials(updatedSelections);
                      onChange(updatedSelections);
                    }}
                    disabled={!referentialSelection.global_referential_category_id}
                  >
                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText placeholder={t('indicator.filter.subCategory.select')} />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.ClearTrigger />
                        <Select.Indicator />
                      </Select.IndicatorGroup>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content>
                        {subCategoriesCollection.items.map((subCategory) => (
                          <Select.Item item={subCategory} key={subCategory.name}>
                            {subCategory.name}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                )}
                {!disabledAdd && <Separator mt={2} />}
              </Box>
            );
          })}

          {/* Button to Add New Referential */}
          {!disabledAdd && (
            <Box>
              <Button colorScheme="blue" size={size} onClick={handleAddReferential} w="full">
                <FaPlus /> {t('indicator.addNewReferential')}
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </>
  );
};

export default SelectReferentials;
